import { Table, TableBody } from "@mui/material";
import { useMemo } from "react";
import { HeadingM } from "../Typography";
import { BasicTableHead, BasicTableRow } from "./Components";
import * as Styled from "./TableBasic.styled";
import { EnumCellType } from "./TableHelpers";
import {
    GenericComponent,
    RequiredHeadProps,
    RequiredRowProps,
    TableBasicProps,
    TableBasicPropsWithDefaults,
} from "./TableTypes";

// Basic table without any "load more" and "filter" buttons

/**
 * Basic table with support
 * Takes in {@link TableBasicProps}. For inserting arrays of objects please use {@link SerializeObjects} function.
 *
 * Minimal variables that need to be provieded are:
 * @param head - contains information about individual columns
 * @param data - contains data for the individual rows
 */

export const defaultTableComponents: TableBasicPropsWithDefaults["components"] = {
    Head: BasicTableHead as GenericComponent<RequiredHeadProps>,
    Row: BasicTableRow as GenericComponent<RequiredRowProps>,
};

export const TableBasic = (props: TableBasicProps) => {
    const defaultProps: TableBasicPropsWithDefaults = {
        ...props,
        priorityCol: props.priorityCol ?? 1,
        alignRightIndex: props.alignRightIndex ?? props.columnTitles.length + 1,
        types: props.types ?? Array.from<EnumCellType>({ length: props.columnTitles.length }),
        components: {
            ...defaultTableComponents,
            ...props.components,
        },
    };
    const { Head, Row, SumRow } = defaultProps.components ?? {};

    const title = useMemo(() => {
        if (!defaultProps.title) return undefined;
        if (typeof defaultProps.title === "string") return defaultProps.title;
        if (defaultProps.title?.props?.title && typeof defaultProps.title.props.title === "string")
            return defaultProps.title.props.title;
        return undefined;
    }, [defaultProps.title]);

    return (
        defaultProps.data.length > 0 && (
            <Styled.TableWrapper>
                {defaultProps.title &&
                    (typeof defaultProps.title === "string" ? (
                        <HeadingM strong scaleDown>
                            {defaultProps.title}
                        </HeadingM>
                    ) : (
                        defaultProps.title
                    ))}
                <Table>
                    {/* Head of the table */}
                    {Head && <Head {...defaultProps} />}
                    {/* Contents of the table */}
                    <TableBody>
                        {defaultProps.data.map(
                            (row, rowKey) =>
                                Row && <Row {...defaultProps} title={title} key={rowKey} rowKey={rowKey} />,
                        )}
                        {SumRow && <SumRow {...defaultProps} />}
                    </TableBody>
                </Table>
                {defaultProps.footer}
            </Styled.TableWrapper>
        )
    );
};
